import React from 'react';
import { ApolloError } from '@apollo/client';
import { Stack } from '@mui/material';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { MembershipReferralsController } from '@occc/feat-membership-referrals';
import config from '@occc/config';
import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';
import {
  LocationFragment,
  MembershipFragment,
  Membership_State,
  PhoneNumberMetadataFragment,
} from '@occc/graphql';
import { IEaivMembershipStatistics } from '@occc-app/dataAccessLayer/eaiv/types';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { CreateCommentModal } from '@occc-app/routes/Member/MemberDetailsPage/components/CreateCommentModal';
import { useTabQueryParam } from '@occc/utils-query-params';
import { useChainConfiguration } from '@occc-app/Chain/hooks';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { CSRContainer } from '@occc/feat-csr';
import { Actions } from './components/Actions';
import { MemberContent } from './components/MemberContent';
import { ProgramContent } from './components/ProgramContent';
import { WalletsContent } from './components/WalletsContent';
import { MemberEmployeeDetails as EmployeeContent } from './components/MemberEmployeeDetails';
import { CustomDataContent } from './components/custom-data';
import { TabContent, Tabs, Tab } from './components/Tabs';
import EditModal from './components/EditModal';
import UpdatePointsModal from './components/UpdatePointsModal';
import { MemberStateMenuContainer } from './components/MemberDetails/MemberStateMenuContainer';
import MemberTypeDropdown from './components/MemberDetails/MemberTypeDropdown';
import { useLinks } from './hooks';
import { useMembershipEmployeeData } from './hooks/useMembershipEmployeeData';

import { MemberDetailsPageTab } from './types';
import {
  MEMBER_DETAILS_TAB_PARAM,
  DEFAULT_MEMBER_DETAILS_TAB,
  MEMBER_DETAILS_TABS,
} from './constants';

import { displayFullName } from '../utils';

type MemberDetailsPageProps = {
  toggleEditModal: () => void;
  toggleUpdatePointsModal: () => void;
  toggleCreateCommentModal: () => void;
  member: MembershipFragment;
  eaivStatistics: IEaivMembershipStatistics;
  locationsLoading: boolean;
  showEditModal: boolean;
  showUpdatePointsModal: boolean;
  showCreateCommentModal: boolean;
  isPointsAdjustmentEnabled: boolean;
  locations?: LocationFragment[];
  locationsError?: ApolloError;
  employeeData: ReturnType<typeof useMembershipEmployeeData>;
  refetchMembership: () => void;
  phoneNumberMetadata?: PhoneNumberMetadataFragment;
  isPhoneNumberMetadataLoading: boolean;
  isPhoneNumberMetadataError?: ApolloError;
  isCSRLoginEnabled: boolean;
  isCSRLoginStarted: boolean;
  toggleIsCSRLoginStarted: () => void;
  onCSRLoginCancelled: () => void;
};

export const MemberDetailsPage = (props: MemberDetailsPageProps) => {
  const [activeTab, setActiveTab] = useTabQueryParam<MemberDetailsPageTab>(
    MEMBER_DETAILS_TAB_PARAM,
    DEFAULT_MEMBER_DETAILS_TAB,
    MEMBER_DETAILS_TABS,
  );
  const {
    toggleEditModal,
    toggleUpdatePointsModal,
    toggleCreateCommentModal,
    member,
    showEditModal,
    showUpdatePointsModal,
    showCreateCommentModal,
    locations,
    locationsLoading,
    locationsError,
    eaivStatistics,
    isPointsAdjustmentEnabled,
    employeeData,
    refetchMembership,
    phoneNumberMetadata,
    isPhoneNumberMetadataLoading,
    isPhoneNumberMetadataError,
    isCSRLoginEnabled,
    isCSRLoginStarted,
    toggleIsCSRLoginStarted,
    onCSRLoginCancelled,
  } = props;

  useAppLayoutContextConsumer(useLinks(member));
  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.MEMBER_EDIT);
  const isUserPermittedToEditCustomData = useIsUserPermittedTo(Feature.MEMBER_CUSTOM_DATA_EDIT);
  const { referralProgramConfigurationEnabled } = useChainConfiguration();

  const handleChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: MemberDetailsPageTab,
  ) => {
    setActiveTab(value);
  };

  const isEditable = member.state !== Membership_State.Deleted;
  const isCustomDataEditable =
    isEditable && isUserPermittedToEdit && isUserPermittedToEditCustomData;

  return (
    <Page
      browserTabTitle='Member'
      pageHeaderProps={{
        title: displayFullName(member),
        subtitle: (
          <Stack spacing={1} direction='row'>
            <MemberStateMenuContainer
              member={member}
              isUserPermittedToUpdateMemberState={isUserPermittedToEdit}
              refetchMembership={refetchMembership}
            />
            <MemberTypeDropdown
              member={member}
              isUserPermittedToUpdateMemberType={isUserPermittedToEdit}
            />
          </Stack>
        ),
      }}
      actions={
        <Actions
          toggleEditModal={toggleEditModal}
          isUserPermittedToEdit={isEditable && isUserPermittedToEdit}
          onOpenExperienceClick={toggleIsCSRLoginStarted}
          isCSRLoginEnabled={isCSRLoginEnabled}
        />
      }
      containerProps={{ variant: 'md' }}
    >
      <Tabs value={activeTab} onChange={handleChange}>
        <Tab value={'details' satisfies MemberDetailsPageTab} label='Member Details' wrapped />
        <Tab value={'program' satisfies MemberDetailsPageTab} label='Program' />
        {config.features.walletSteeringEnabled && (
          <Tab value={'wallets' satisfies MemberDetailsPageTab} label='Wallets' />
        )}
        {employeeData.isEmployeeDetailsTabVisible && (
          <Tab value={'employee' satisfies MemberDetailsPageTab} label='Employee Details' />
        )}
        {referralProgramConfigurationEnabled && (
          <Tab value={'referrals' satisfies MemberDetailsPageTab} label='Referrals' />
        )}
        <Tab value={'customData' satisfies MemberDetailsPageTab} label='Custom Data' />
      </Tabs>
      <>
        <TabContent id={'details' satisfies MemberDetailsPageTab} value={activeTab}>
          <MemberContent
            toggleUpdatePointsModal={toggleUpdatePointsModal}
            toggleCreateCommentModal={toggleCreateCommentModal}
            member={member}
            phoneNumberMetadata={phoneNumberMetadata}
            isPhoneNumberMetadataLoading={isPhoneNumberMetadataLoading}
            isPhoneNumberMetadataError={isPhoneNumberMetadataError}
            locations={locations}
            locationsLoading={locationsLoading}
            locationsError={locationsError}
            isPointsAdjustmentEnabled={isPointsAdjustmentEnabled}
            eaivStatistics={eaivStatistics}
            isUserPermittedToEdit={isUserPermittedToEdit}
          />
        </TabContent>
        <TabContent id={'program' satisfies MemberDetailsPageTab} value={activeTab}>
          <ProgramContent member={member} />
        </TabContent>
        {config.features.walletSteeringEnabled && (
          <TabContent id={'wallets' satisfies MemberDetailsPageTab} value={activeTab}>
            <WalletsContent memberId={member.id} />
          </TabContent>
        )}
        {employeeData.isEmployeeDetailsTabVisible && (
          <TabContent id={'employee' satisfies MemberDetailsPageTab} value={activeTab}>
            <EmployeeContent employeeData={employeeData} />
          </TabContent>
        )}
        {referralProgramConfigurationEnabled && (
          <TabContent id={'referrals' satisfies MemberDetailsPageTab} value={activeTab}>
            <MembershipReferralsController membershipId={member.id} />
          </TabContent>
        )}
        <TabContent id={'customData' satisfies MemberDetailsPageTab} value={activeTab}>
          <CustomDataContent
            data={member.customData}
            memberId={member.id}
            customerUuid={member.customerUuid}
            canEdit={isCustomDataEditable}
          />
        </TabContent>
      </>
      <>
        <EditModal
          onHide={toggleEditModal}
          member={member}
          show={showEditModal}
          locations={locations}
        />
        {isCSRLoginEnabled && member.phone && (
          <CSRContainer
            isCSRLoginStarted={isCSRLoginStarted}
            memberPhone={member.phone}
            onCSRLoginCancelled={onCSRLoginCancelled}
          />
        )}
        {member.customerUuid && (
          <UpdatePointsModal
            onHide={toggleUpdatePointsModal}
            show={showUpdatePointsModal}
            customerUuid={member.customerUuid}
          />
        )}
        {member.customerUuid && (
          <CreateCommentModal
            isOpen={showCreateCommentModal}
            onClose={toggleCreateCommentModal}
            customerUuid={member.customerUuid}
          />
        )}
      </>
    </Page>
  );
};
